<template>
  <div>
    <VForm autocomplete="off" @submit.prevent>
      <VRow>
        <VCol class="mb-2">
          <VTextField
            id="firstName"
            v-model="firstName"
            autofocus
            outlined
            dense
            autocomplete="given-name"
            :error-messages="errors.firstName"
            @input="clearError('firstName')"
          >
            <template v-slot:label>
              {{ $t('firstName') }} *
            </template>
          </VTextField>
          <VTextField
            id="lastName"
            v-model="lastName"
            outlined
            dense
            autocomplete="family-name"
            :error-messages="errors.lastName"
            @input="clearError('lastName')"
          >
            <template v-slot:label>
              {{ $t('lastName') }} *
            </template>
          </VTextField>
          <VTextField
            id="email"
            v-model="email"
            type="email"
            outlined
            dense
            label="E-mail"
            autocomplete="email"
            :error-messages="errors.email"
            @input="clearError('email')"
          >
            <template v-slot:label>
              E-mail *
            </template>
          </VTextField>
          <PhoneNumberInput
            id="phone"
            :key="country"
            v-model="phone"
            :label="$t('phone')"
            :default-country="country"
            :auto-default-country="false"
            :error-messages="errors.phone"
            @input="clearError('phone')"
          />
          <VTextField
            id="resume"
            v-model="resumeName"
            type="resume"
            outlined
            dense
            readonly
            :label="$t('resume')"
            :rules="resumeRules"
            clearable
            prepend-inner-icon="mdi-file-outline"
            hide-details
            @click="addFile"
            @click:prepend-inner="addFile"
            @click:clear="clearResume"
          >
            <template v-slot:label>
              {{ $t('resume') }}
            </template>
          </VTextField>
          <template v-show="false">
            <input
              v-show="false"
              ref="resumeInput"
              type="file"
              :accept="resumeAvailableTypes"
              @change="handleFileUpload( $event )"
            >
          </template>
        </VCol>
        <VCol v-if="searchedCandidates" class="mb-2">
          <VCard style="height: 100%; margin-right: 2px">
            <VCardTitle>
              {{ $t('searchTitle') }}
            </VCardTitle>
            <VCardText v-if="searchedCandidates.length === 0">
              {{ $t('candidateNotFound') }}
            </VCardText>
            <VList style="height:235px;overflow-y:auto" v-else>
              <VListItem
                v-for="candidate of searchedCandidates"
                :key="candidate.id"
                @click="selectCandidate(candidate)"
              >
                <VListItemContent>
                  <VListItemTitle style="font-size: 0.9rem">{{ candidate.firstName }} {{ candidate.lastName }}</VListItemTitle>
                  <VListItemSubtitle>{{ candidate.email }}</VListItemSubtitle>
                </VListItemContent>
              </VListItem>
            </VList>
          </VCard>
        </VCol>
      </VRow>
      <VRow justify="center">
        <VBtn
          class="ma-2"
          id="inviteBtn"
          color="success"
          :loading="loading"
          @click="submit"
        >
          {{ $t('invite') }}
        </VBtn>
        <VBtn
          class="ma-2"
          color="success"
          outlined
          @click="$emit('cancel')"
        >
          {{ $t('cancel') }}
        </VBtn>
      </VRow>
    </VForm>
  </div>
</template>

<script>
import ValidationMixin from '@/mixins/ValidationMixin';
import RecaptchaMixin from '@/mixins/RecaptchaMixin';
import Candidate from '@/orm/models/Candidate/Candidate';
import { computed } from '@vue/composition-api';
import Company from '@/orm/models/Company/Company';
import { response } from '@/orm/functions';
import { normalizePhoneNumber, objectToFormData } from '@/functions';
import { invitationType } from '@/enums';
import { mdiFileOutline } from '@mdi/js';
import Resume from '@/orm/models/Candidate/Resume';
import PhoneNumberInput from '../components/Input/PhoneNumberInput.vue';

export default {
  name: 'CandidateCreateWithSearch',
  components: { PhoneNumberInput },
  mixins: [ValidationMixin, RecaptchaMixin],
  props: {
    vacancy: {
      type: Object,
      required: true,
    },
  },
  setup() {
    const company = computed(() => {
      return Company.query().with('country').first();
    });

    const country = computed(() => {
      return company.value?.country?.alpha2Code;
    });

    return {
      country,
    };
  },
  data() {
    return {
      firstName: null,
      lastName: null,
      email: null,
      phone: null,
      searchedCandidates: null,
      resume: null,
      mdiFileOutline,
      resumeAvailableTypes: '.pdf,.doc,.docx,.odt,.rtf',
      resumeRules: [
        () => !this.resumeError || this.resumeError,
      ],
      resumeName: null,
      resumeError: null,
    };
  },
  computed: {
    emptyFormValues() {
      return !this.phone && !this.email && !this.firstName && !this.lastName;
    },
  },
  watch: {
    firstName() {
      this.searchWithTimeout();
    },
    lastName() {
      this.searchWithTimeout();
    },
    email() {
      this.searchWithTimeout();
    },
  },
  methods: {
    async submit() {
      await this.withValidationHandling(async () => {
        const recaptchaToken = await this.getRecaptchaToken('candidateCreate');
        const result = await this.storeWithoutInvitation(recaptchaToken);
        if (!result) {
          return;
        }

        const model = await Candidate.find(result.id);
        await this.storeResume(model);
        if ((model && this.withInvitation) || this.isOfficeInterview) {
          this.inviteLink = model.inviteLink;
          window.candidateId = model.id;
        }
        this.$emit('created', model);

        return model;
      });
    },
    async storeWithoutInvitation(recaptchaToken) {
      return response(Candidate.api().storeWithoutInvitation(this.vacancy.id, {
        firstName: this.firstName,
        lastName: this.lastName,
        email: this.email,
        phone: normalizePhoneNumber(this.phone),
        invitationType: invitationType.OFFICE_INTERVIEW,
        recaptchaToken,
      }));
    },
    searchWithTimeout() {
      setTimeout(() => {
        this.searchCandidates();
      }, 500);
    },
    searchCandidates() {
      if (this.emptyFormValues) {
        this.searchedCandidates = null;
        return;
      }

      let query = Candidate.query()
        .withAllRecursive()
        .where('vacancyId', this.vacancy.id);

      if (this.firstName) {
        query = query.where((candidate) => {
          return candidate.firstName
            .toLowerCase()
            .includes(
              this.firstName.toLowerCase(),
            );
        });
      }

      if (this.lastName) {
        query = query.where((candidate) => {
          return candidate.lastName
            .toLowerCase()
            .includes(
              this.lastName.toLowerCase(),
            );
        });
      }

      if (this.email) {
        query = query.where((candidate) => {
          return candidate.email
            .toLowerCase()
            .includes(
              this.email.toLowerCase(),
            );
        });
      }

      this.searchedCandidates = query.get();
    },
    async selectCandidate(candidate) {
      try {
        this.loading = true;
        await Candidate.api().fetchById(candidate.id);
        const selectedCandidate = Candidate.query()
          .withAllRecursive()
          .find(candidate.id);

        this.$emit('selected', selectedCandidate);
        this.loading = false;
      } catch (exception) {
        this.loading = false;
      }
    },
    showCandidate(candidateId) {
      this.$router.push({
        name: 'candidate',
        params: { candidateId },
      });
    },
    async storeResume(candidate) {
      if (this.resume && !this.resumeError) {
        const { resume } = this;
        Resume.api().store(candidate.id, objectToFormData({ resume }));
      }
    },
    addFile() {
      this.$refs.resumeInput.click();
    },
    handleFileUpload(event) {
      this.resumeError = null;
      this.resume = event.target.files[0];
      this.resumeName = this.resume.name;
      if (this.resume.size >= 5000000) {
        this.resumeError = this.$t(`${this.$t('sizeWarning')} 5 MB.`);
      }
    },
    clearResume() {
      this.resumeError = null;
      this.resume = null;
      this.resumeName = null;
      this.$refs.resumeInput.value = '';
    },
  },
};
</script>

<style scoped>

</style>

<i18n locale="en" lang="yaml">
firstName: First name
lastName: Last name
phone: Phone number
invite: Accept
cancel: Cancel
resume: Resume
sizeWarning: The resume should not be larger than
searchTitle: Existing candidates
candidateNotFound: No such candidate was found. A new candidate will be created
</i18n>

<i18n locale="ru" lang="yaml">
firstName: Имя
lastName: Фамилия
phone: Телефон
notifyByEmail: Оповестить по электронной почте
invite: Далее
cancel: Отменить
resume: Резюме
sizeWarning: Размер резюме не должне превышать
searchTitle: Существующие кандидаты
candidateNotFound: Такой кандидат не найден. Будет создан новый кандидат
</i18n>
